/**
 * Deterministic seed + RNG for the Adaptive Cinematic Reveal Engine.
 *
 * Same input string → same seed → same experience on every replay.
 * Different announcements → different seeds → visibly different experiences,
 * even within the same category / theme.
 */

/** djb2-ish 32-bit hash, stable across runtimes. */
export function hashSeed(input: string | undefined | null): number {
  const s = (input ?? 'fallback').toString();
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
    h |= 0;
  }
  // Ensure positive
  return Math.abs(h);
}

/** Mulberry32 PRNG — fast, deterministic, good distribution. */
export function makeRng(seed: number) {
  let a = seed >>> 0;
  return function rng() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface SeededRng {
  /** float in [0, 1) */
  next: () => number;
  /** int in [min, max] inclusive */
  int: (min: number, max: number) => number;
  /** pick element */
  pick: <T>(arr: readonly T[]) => T;
  /** weighted pick — weights need not sum to 1 */
  weighted: <T>(items: readonly T[], weights: readonly number[]) => T;
  /** returns true with prob p */
  chance: (p: number) => boolean;
}

export function createRng(seed: number): SeededRng {
  const rng = makeRng(seed);
  const next = rng;
  return {
    next,
    int: (min, max) => Math.floor(next() * (max - min + 1)) + min,
    pick: (arr) => arr[Math.floor(next() * arr.length)],
    weighted: (items, weights) => {
      const total = weights.reduce((a, b) => a + b, 0);
      let r = next() * total;
      for (let i = 0; i < items.length; i++) {
        r -= weights[i];
        if (r <= 0) return items[i];
      }
      return items[items.length - 1];
    },
    chance: (p) => next() < p,
  };
}
